import styled from "styled-components";
import {
    CorzSelect,
    CorzTextArea,
    CorzTextField,
} from "../../components/form/TextField";
import { useEffect, useRef, useState } from "react";
import { auth, firestore, userData } from "../../../App";
import {
    collection,
    doc,
    getDocs,
    limit,
    orderBy,
    query,
    Timestamp,
    where,
} from "firebase/firestore";
import { ReactComponent as NoEvent } from "../../../res/svg/empty_state_event.svg";
import CheckListItem from "../../components/form/CheckList";
import {
    Divider,
    ElevatedCard,
    InfoAlertBox,
    OutlineCard,
    WaringAlertBox,
} from "../../components/Components";
import {
    FilledButton,
    TextButton,
} from "../../components/buttons/CommonButton";
import ModalDialogComponent from "../../components/dialog/ModalDialog";
import PostFinanceLogo from "../../../res/image/postfinance.png";
import TwintLogo from "../../../res/image/twint.webp";
import GiftLogo from "../../../res/image/gift.png";
import Checkbox from "../../components/form/CheckBox";
import FullScreenDialog from "../../components/dialog/FullScreenDialog";
import {
    daysBetweenDates,
    formatDateServer,
    stringToDate,
} from "../../../tools/DateTool";
import { formatFranc, formatThousand } from "../../../tools/NumberTools";
import {
    deleteEvent,
    getEvent,
    pushKW,
    pushNote,
    updateDocument,
    updateEvent,
} from "../../../tools/FirestoreTool";
import { eventEmitter } from "../../../tools/EventEmitter";
import SnackbarComponent from "../../components/dialog/SnackbarComponent";
import Loader from "../../components/Loader";

const Container = styled.div`
	position: relative;
	width: 100%;
	height: 100%;
	padding: 12px 24px;
	margin-top: 12px;
	-webkit-user-select: none;
	user-select: none;

	h2 {
		margin-bottom: 12px;
	}

	@media screen and (max-width: 768px) {
		padding: 12px;
	}

	@media screen and (max-width: 400px) {
		padding: 12px 6px;
	}
`;

const EmptyStateContainer = styled.section``;

const ManageStateContainer = styled.section``;

const SummaryContent = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: baseline;
`;

const ManageContent = styled.div`
	display: flex;
	flex-direction: column;
	margin-top: 24px;
	gap: 24px;
`;

const ManageCard = styled(OutlineCard)`
	display: flex;
	flex-direction: column;
	gap: 12px;
	width: 100%;

	h3 {
		margin-bottom: 12px;
	}

	table caption {
		text-align: start;
	}

	table tr:last-child td:last-child {
		border-top: 1px solid var(--color-outline);
		font-weight: bold;
	}

	tr td:last-child {
		text-align: end;
	}
`;

const InputRowContainer = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-around;
	gap: 12px;
	flex-wrap: wrap;
	margin-bottom: 12px;
`;

const PaymentCard = styled(ElevatedCard)`
	display: flex;
	flex-direction: row;
	width: 100%;
	align-items: center;
	gap: 24px;
	border: ${(props) =>
        props.isSelected ? "1px solid var(--color-surface-tint)" : "none"};
	background: var(
		${(props) =>
        props.isSelected
            ? "--color-surface-variant"
            : "--color-surface-container-low"}
	);
	color: var(
		${(props) =>
        props.isSelected ? "--color-surface-tint" : "--color-on-surface"}
	);
	transition: 0.3s;

	img {
		width: 36px;
		height: 36px;
		object-fit: contain;
		border-radius: 6px;
	}
`;

const ChecklistTitle = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
	height: 36px;
	color: var(--color-surface-tint);
	background: var(--color-surface-container-high);
	padding: 0 12px;
	border-radius: 6px;

	span {
		width: 16px;
		height: 16px;
		font-size: 16px;
		line-height: 16px;
	}
`;

const ChecklistContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	text-align: start;
	margin: 6px 0 12px;

	div {
		justify-content: start;
	}
`;

const ButtonContainer = styled.div`
    display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	gap: 12px;
	justify-content: end;
	align-items: center;
    margin-top: 24px;
`;

function copyToClipboard(text) {
    navigator.clipboard.writeText(text).then(
        function () {
            console.log("Testo copiato negli appunti con successo!");
        },
        function (err) {
            console.error("Errore nel copiare il testo: ", err);
        }
    );
}

function RecordViewPager() {
    const [localUserData, setLocalUserData] = useState(userData);
    const [eventData, setEventData] = useState([]);
    const [entryData, setEntryData] = useState([]);
    const [currentEvent, setCurrentEvent] = useState(null);
    const [lastKiloWatt, setLastKiloWatt] = useState(0);
    const [manage, setManage] = useState("");
    const [paymentMethod, setPaymentMethod] = useState(0);
    const [totalNights, setTotalNights] = useState(0);

    const [trySaving, setTrySaving] = useState(false);
    const [tryDeleting, setTryDeleting] = useState(false);

    const selectRef = useRef(null);
    const dialogRef = useRef(null);
    const fullDialogRef = useRef(null);
    const checklistRef = useRef(null);
    const checkinKwDay = useRef(null);
    const checkinKwNight = useRef(null);
    const checkinNote = useRef(null);
    const checkoutKwDay = useRef(null);
    const checkoutKwNight = useRef(null);
    const checkoutNote = useRef(null);

    useEffect(() => {
        const handleUserDataUpdated = (data) => {
            setLocalUserData(data);
        };

        eventEmitter.on("userDataUpdated", handleUserDataUpdated);

        return () => {
            eventEmitter.off("userDataUpdated", handleUserDataUpdated);
        };
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            const eventRef = collection(firestore, "events");
            const eventQuery = query(
                eventRef,
                where("uid", "==", auth.currentUser.uid),
                orderBy("timestampStartDate", "desc")
            );

            let eventData = [];
            let entryData = [];

            const eventQuerySnapshot = await getDocs(eventQuery);
            eventQuerySnapshot.forEach((doc) => {
                console.log("doc loaded", doc.data());

                eventData.push({ doc_id: doc.id, ...doc.data() });
                entryData.push({
                    key: doc.id,
                    value: `${doc.data().startDate} - ${doc.data().endDate}`,
                });
            });

            setEventData(eventData);
            setEntryData([{ key: "", value: "" }, ...entryData]);

            // Load last kw
            const kwRef = collection(firestore, "kilowatt");
            const kwQuery = query(
                kwRef,
                orderBy("timestamp", "desc"),
                limit(1)
            );

            const kwQuerySnapshot = await getDocs(kwQuery);
            kwQuerySnapshot.forEach((doc) => {
                console.log("doc loaded", doc.data());

                setLastKiloWatt(doc.data().kw);
            });
        };

        if (auth.currentUser) {
            fetchData();
        }
    }, [auth.currentUser]);

    const handleOnSelectChange = (ref, value) => {
        if (value === "") {
            setCurrentEvent(null);
            return;
        }

        const eventManaged = eventData.find((event) => event.doc_id === value);
        console.log(value, eventManaged);

        if (eventManaged.checkout) {
            setTotalNights(
                daysBetweenDates(
                    stringToDate(eventManaged.startDate),
                    stringToDate(eventManaged.endDate)
                )
            );
            setManage("payment");
        } else if (eventManaged.checkin) {
            setManage("checkout");
        } else {
            setManage("checkin");
        }

        setCurrentEvent(eventManaged);
    };

    const handleOnSubmit = async (e) => {
        e.preventDefault();
        console.log("Submitting form...", manage);

        if (localUserData == null) {
            console.error("No user data was provided");
            return;
        }

        if (manage === "checkin") {
            await doCheckInRegistration(new FormData(e.target));
        } else if (manage === "checkout") {
            const startDate = stringToDate(currentEvent.startDate);
            const endDate = stringToDate(currentEvent.endDate);

            const daysCost = daysBetweenDates(startDate, endDate) * 50;
            const eventCost = Number(currentEvent.cost) || 0.0;
            console.log(`DayCost: ${daysCost}; EventCost: ${eventCost}`);

            if (daysCost < eventCost) {
                // If the calculated cost is less than the event cost apply the event cost
                await doCheckOutRegistration(new FormData(e.target), eventCost);
            } else {
                // If the calculated cost is greater than the event cost apply the calculated cost
                //! That will prevent error while calculating costs
                await doCheckOutRegistration(new FormData(e.target), daysCost);
            }
        } else {
            console.error("Invalid manage state: ", manage);
        }
    };

    const handleOnPay = () => {
        setTrySaving(true);

        try {
            updateEvent(currentEvent.doc_id, {
                payed: true,
            });

            if (paymentMethod === "gift") {
                updateDocument("users", localUserData.uid, {
                    gift_used: true,
                });
            }

            eventEmitter.emit("showNotification", {
                title: "Pagamento registrato",
                text: "Il pagamento è stato registrato!",
            });

            setCurrentEvent(prev => ({ ...prev, payed: true }));

            setEventData(prevList => prevList.map(ev =>
                ev.doc_id === currentEvent.doc_id ? { ...ev, payed: true } : ev
            ));
        } catch (error) {
            console.error("Error pushing document: ", error);

            eventEmitter.emit("showNotification", {
                title: "Impossibile registrare il pagamento",
                text: "Il pagamento non è stato registrato.",
            });
        } finally {
            setTrySaving(false);
        }
    };

    const handleOnDelete = () => {
        setTryDeleting(true);

        try {
            const docId = currentEvent.doc_id;
            deleteEvent(currentEvent.doc_id);

            setEventData(prev => prev.filter((event) => event.doc_id !== docId));
            setEntryData(prev => prev.filter((entry) => entry.key !== docId));

            setCurrentEvent(null);
            setManage("");

            eventEmitter.emit("showNotification", {
                title: "Prenotazione eliminata",
                text: "La prenotazione è stata eliminata!",
            });

            dialogRef.current.close();
        } catch (error) {
            console.error("Error deleting document: ", error);

            eventEmitter.emit("showNotification", {
                title: "Impossibile eliminare la prenotazione",
                text: "La prenotazione non è stata eliminata.",
            });
        } finally {
            setTryDeleting(false);
        }
    };

    const doCheckInRegistration = async (formData) => {
        const kwDay = Number(formData.get("kw-day-in"));
        const kwNight = Number(formData.get("kw-night-in"));
        const note = String(formData.get("checkin-note"));

        if (kwDay + kwNight < lastKiloWatt) {
            checkinKwDay.current.updateError(true, "Dati non corretti");
            checkinKwNight.current.updateError(true, "Dati non corretti");
            return;
        }

        const kwSum = kwDay + kwNight;
        const updateEventData = {
            checkin: true,
            kwUsed: 0,
        };

        const success = await uploadUpdatedEventData(updateEventData, kwSum, note);

        if (!success) {
            eventEmitter.emit("showNotification", {
                title: "Impossibile registrare il check-in",
                text: "Il check-in non è stato registrato.",
            });
            return;
        }

        eventEmitter.emit("showNotification", {
            title: "Check-in registrato",
            text: "Il check-in è stato registrato!",
        });

        setCurrentEvent(prev => ({ ...prev, checkin: true }));
        setManage("");
        setTrySaving(false);
    }

    const doCheckOutRegistration = async (formData, eventCost) => {
        const kwDay = Number(formData.get("kw-day-out"));
        const kwNight = Number(formData.get("kw-night-out"));
        const note = String(formData.get("checkout-note"));
        const checklist = Boolean(formData.get("checklist"));

        if (kwDay + kwNight < lastKiloWatt) {
            console.log("KW data incorrect:", kwDay + kwNight, "<", lastKiloWatt);
            checkoutKwDay.current.updateError(true, "Dati non corretti");
            checkoutKwNight.current.updateError(true, "Dati non corretti");
            return;
        }

        if (!checklist) {
            console.log("Checklist not completed");
            checklistRef.current.setError();
            return;
        }

        const kwSum = kwDay + kwNight;
        const kwUsed = kwSum - lastKiloWatt;
        const updateEventData = {
            checkout: true,
            kwUsed: kwUsed,
            cost: eventCost + kwUsed * 0.3,
        };

        console.log("kwSum:", kwSum, "lastKiloWatt:", lastKiloWatt, "kwUsed:", kwUsed);
        console.log("Updating event data:", updateEventData);

        const success = await uploadUpdatedEventData(updateEventData, kwSum, note);

        if (!success) {
            eventEmitter.emit("showNotification", {
                title: "Impossibile registrare il check-out",
                text: "Il check-out non è stato registrato.",
            });
            return;
        }

        const updatedData = await getEvent(currentEvent.doc_id);
        setCurrentEvent(prev => ({ ...prev, ...updatedData }));

        setTotalNights(
            daysBetweenDates(
                stringToDate(updatedData.startDate),
                stringToDate(updatedData.endDate)
            )
        );

        eventEmitter.emit("showNotification", {
            title: "Check-out registrato",
            text: "Il check-out è stato registrato!",
        });

        setManage("payment");
    }

    const uploadUpdatedEventData = async (eventData, kwCount, note) => {
        console.log("documentId", currentEvent.doc_id, "updateEventData", eventData);
        setTrySaving(true);

        /* Save kilowatt data [START] */
        console.log("Saving kilowatt: ", kwCount);

        try {
            const kwData = {
                eventOf: currentEvent.doc_id,
                kw: kwCount,
                timestamp: Timestamp.fromDate(new Date()),
                uid: auth.currentUser.uid,
            };

            pushKW(kwData);
        } catch (error) {
            console.error("Error pushing kilowatt data: ", error);
            eventEmitter.emit("showNotification", {
                title: "Impossibile registrare i kilowatt",
                text: "I kilowatt non sono stati registrati.",
            });

            setTrySaving(false);
            return false;
        }
        /* Save kilowatt data [END] */

        /* Save note data [START] */
        if (note !== "") {
            try {
                const noteData = {
                    eventOf: `${manage === "checkin" ? "Check-In" : "Check-Out"
                        } ${formatDateServer(new Date())}`,
                    nome: localUserData.nome,
                    reactionist: [],
                    text: note,
                    timestamp: Timestamp.fromDate(new Date()),
                    uid: auth.currentUser.uid,
                };

                pushNote(noteData);
            } catch (error) {
                console.error("Error pushing note data: ", error);
                eventEmitter.emit("showNotification", {
                    title: "Impossibile registrare la nota",
                    text: "La nota non è stata registrata.",
                });

                setTrySaving(false);
                return false;
            }
        }
        /* Save note data [END] */

        /* Save event data [START] */
        try {
            updateEvent(currentEvent.doc_id, eventData);
        } catch (error) {
            console.error("Error pushing event data: ", error);
            eventEmitter.emit("showNotification", {
                title: "Impossibile registrare l'evento",
                text: "L'evento non è stato registrato.",
            });

            setTrySaving(false);
            return false;
        }
        /* Save event data [END] */

        // Return success
        setTrySaving(false);
        return true;
    }

    return (
        <Container>
            <h2>Gestione prenotazioni</h2>
            <p>
                Seleziona la prenotazione che vuoi gestire, dopodiché potrai
                effettuare la registrazione di check-in, check-out o pagamento
            </p>
            <CorzSelect
                ref={selectRef}
                id="event-select"
                label="Seleziona prenotazione"
                limited={false}
                entryList={entryData}
                onSelectChange={handleOnSelectChange}
            />
            {currentEvent ? (
                <ManageStateContainer>
                    <SummaryContent>
                        <CheckListItem
                            checkable={false}
                            isChecked={!!currentEvent.checkin}
                            label="Check-in"
                        />
                        <CheckListItem
                            checkable={false}
                            isChecked={!!currentEvent.checkout}
                            label="Check-out"
                        />
                        <CheckListItem
                            checkable={false}
                            isChecked={!!currentEvent.payed}
                            label="Pagato"
                        />
                    </SummaryContent>
                    {manage === "checkin" && (
                        <ManageContent>
                            <ManageCard notHover={true}>
                                <h3>Registra Check-In (Arrivo)</h3>
                                {currentEvent.servicing && (
                                    <InfoAlertBox>
                                        <span className="material-symbol fill">
                                            info
                                        </span>
                                        <span>
                                            <b>Informazione</b>
                                            <br />
                                            Questa prenotazione è per scopi di
                                            lavoro/manutenzione, registra
                                            comunque i kW!
                                        </span>
                                    </InfoAlertBox>
                                )}
                                <form onSubmit={handleOnSubmit} noValidate>
                                    <h4>Registra i kW</h4>
                                    <p>
                                        Spostati tra kW diurni e notturni con il
                                        bottone "Seq. Manuale Display" in alto a
                                        sinistra al contatore. Purtroppo il contatore
                                        non dice quale valore è diurno e quale notturno.
                                        Cliccando una volta il bottone dovresti ottenere
                                        i kw diurni, in seguito cliccando ancora il
                                        bottone troverai i kw notturni.
                                    </p>
                                    <p>
                                        Ultima registrazione:{" "}
                                        <b>{formatThousand(lastKiloWatt)}kw</b>.
                                        Inserendo i nuovi kw dovresti ottenere
                                        una somma maggiore ai kw precedenti.
                                    </p>
                                    <InputRowContainer>
                                        <CorzTextField
                                            ref={checkinKwDay}
                                            id="kw-day-in"
                                            label="kW Diurni*"
                                            type="number"
                                            suffixText="kW"
                                            value="0"
                                            required
                                        />
                                        <CorzTextField
                                            ref={checkinKwNight}
                                            id="kw-night-in"
                                            label="kW Notturni*"
                                            type="number"
                                            suffixText="kW"
                                            value={0}
                                            required
                                        />
                                    </InputRowContainer>
                                    <Divider
                                        style={{ margin: "12px 8px 8px" }}
                                    />
                                    <h4>Aggiungi una nota</h4>
                                    <p>
                                        Se necessario aggiungi una nota, questa
                                        verrà mostrata con la data della
                                        prenotazione.
                                    </p>
                                    <CorzTextArea
                                        ref={checkinNote}
                                        id="checkin-note"
                                        limited={false}
                                    />
                                    <ButtonContainer>
                                        {trySaving && <Loader size={24} />}
                                        <FilledButton
                                            text="Salva"
                                            type="submit"
                                            isDisabled={trySaving}>
                                            <span className="material-symbol fill">
                                                check
                                            </span>
                                        </FilledButton>
                                    </ButtonContainer>
                                </form>
                            </ManageCard>
                            <ManageCard notHover={true}>
                                <h3 style={{ color: "var(--color-error)" }}>
                                    Annulla prenotazione
                                </h3>
                                <p>
                                    Puoi annullare la prenotazione solo prima di
                                    effettuare il checkin.
                                </p>
                                <WaringAlertBox>
                                    <span className="material-symbol fill">
                                        warning
                                    </span>
                                    <span>
                                        <b>Attenzione!</b>
                                        <br />
                                        Una volta annullata non potrai più
                                        accedere a questa prenotazione.
                                    </span>
                                </WaringAlertBox>
                                <ButtonContainer>
                                    {tryDeleting && <Loader size={24} />}
                                    <FilledButton
                                        text="Elimina"
                                        theme="error"
                                        isDisabled={tryDeleting}
                                        onClick={() => {
                                            dialogRef.current.open();
                                        }}
                                    >
                                        <span className="material-symbol fill">
                                            delete
                                        </span>
                                    </FilledButton>
                                </ButtonContainer>
                            </ManageCard>
                        </ManageContent>
                    )}
                    {manage === "checkout" && (
                        <ManageContent>
                            <ManageCard notHover={true}>
                                <h3>Registra Check-Out (Partenza)</h3>
                                {currentEvent.servicing && (
                                    <InfoAlertBox>
                                        <span className="material-symbol fill">
                                            info
                                        </span>
                                        <span>
                                            <b>Informazione</b>
                                            <br />
                                            Questa prenotazione è per scopi di
                                            lavoro/manutenzione, registra
                                            comunque i kW!
                                        </span>
                                    </InfoAlertBox>
                                )}
                                <form onSubmit={handleOnSubmit} noValidate>
                                    <h4>Registra i kW</h4>
                                    <p>
                                        Spostati tra kW diurni e notturni con il
                                        bottone "Seq. Manuale Display" in alto a
                                        sinistra al contatore. Ultima
                                        registrazione:{" "}
                                        <b>{formatThousand(lastKiloWatt)}kw</b>.
                                        Inserendo i nuovi kw dovresti ottenere
                                        una somma maggiore ai kw precedenti.
                                    </p>
                                    <InputRowContainer>
                                        <CorzTextField
                                            ref={checkoutKwDay}
                                            id="kw-day-out"
                                            label="kW Diurni*"
                                            type="number"
                                            suffixText="kW"
                                            value="0"
                                            required
                                        />
                                        <CorzTextField
                                            ref={checkoutKwNight}
                                            id="kw-night-out"
                                            label="kW Notturni*"
                                            type="number"
                                            suffixText="kW"
                                            value={0}
                                            required
                                        />
                                    </InputRowContainer>
                                    <Divider
                                        style={{ margin: "12px 8px 8px" }}
                                    />
                                    <h4>Checklist di pulizia</h4>
                                    <p>
                                        Un modo per lasciare la casa in ordine e
                                        pulita è seguire questa checklist che ti
                                        aiuta a ricordare tutto quello che devi
                                        fare prima di andare via. Oltre alle
                                        operazioni di routine, come pulire i
                                        pavimenti, spolverare i mobili e pulire
                                        i davanzali interni delle finestre, ecco
                                        una lista di cose da controllare e
                                        completare. Ricorda anche di non
                                        lasciare niente di tuo nella casa, come
                                        oggetti personali, cibo o rifiuti.
                                    </p>
                                    <p>
                                        Una volta completato tutto ricordati di
                                        chiudere l'acqua e di spegnere la
                                        corrente, nel dubbio puoi controllare in
                                        "tutorial" come fare.
                                    </p>
                                    <ButtonContainer>
                                        <TextButton
                                            text="Consulta checklist"
                                            type="button"
                                            onClick={() =>
                                                fullDialogRef.current.open()
                                            }
                                        >
                                            <span className="material-symbol fill">
                                                checklist
                                            </span>
                                        </TextButton>
                                    </ButtonContainer>
                                    <Checkbox
                                        ref={checklistRef}
                                        id="checklist"
                                        label="Confermo di aver eseguito/controllota in
										modo che sia tutto in ordine"
                                    />
                                    <Divider
                                        style={{ margin: "12px 8px 8px" }}
                                    />
                                    <h4>Aggiungi una nota</h4>
                                    <p>
                                        Se necessario aggiungi una nota, questa
                                        verrà mostrata con la data della
                                        prenotazione.
                                    </p>
                                    <CorzTextArea
                                        ref={checkoutNote}
                                        id="checkout-note"
                                        limited={false}
                                    />
                                    <ButtonContainer>
                                        {trySaving && <Loader size={24} />}
                                        <FilledButton
                                            text="Salva"
                                            type="submit"
                                            isDisabled={trySaving}>
                                            <span className="material-symbol fill">
                                                check
                                            </span>
                                        </FilledButton>
                                    </ButtonContainer>
                                </form>
                            </ManageCard>
                        </ManageContent>
                    )}
                    {manage === "payment" && (
                        <ManageContent>
                            <ManageCard notHover={true}>
                                <h3>Pagamento</h3>
                                <table>
                                    <caption>Riepilogo</caption>
                                    <tbody>
                                        <tr>
                                            <td>
                                                {totalNights} notti/e x CHF
                                                50.-:
                                            </td>
                                            <td>
                                                {formatFranc(totalNights * 50)}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                Kilowatt ({currentEvent.kwUsed}
                                                kW * 0.3):
                                            </td>
                                            <td>
                                                {formatFranc(
                                                    (currentEvent.kwUsed ||
                                                        0.0) * 0.3
                                                )}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Somma totale:</td>
                                            <td>
                                                {formatFranc(
                                                    currentEvent.cost || 0.0
                                                )}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <PaymentCard
                                    isSelected={paymentMethod === 0}
                                    onClick={() => setPaymentMethod(0)}
                                >
                                    <img
                                        src={PostFinanceLogo}
                                        alt="postfinance"
                                    />
                                    <h4>Conto bancario</h4>
                                </PaymentCard>
                                <PaymentCard
                                    isSelected={paymentMethod === 1}
                                    onClick={() => setPaymentMethod(1)}
                                >
                                    <img src={TwintLogo} alt="twint" />
                                    <h4>Twnint</h4>
                                </PaymentCard>
                                <PaymentCard
                                    isDisabled={localUserData.gift_used | false}
                                    isSelected={paymentMethod === 2}
                                    onClick={() => setPaymentMethod(2)}
                                >
                                    <img src={GiftLogo} alt="gift" />
                                    <h4>Buono per un week-end</h4>
                                </PaymentCard>
                                {paymentMethod === 0 && (
                                    <>
                                        <p style={{ margin: "0" }}>
                                            Cliccando sul testo puoi copiare
                                            l'informazioni sulla riga:
                                        </p>
                                        <p>
                                            <span
                                                onClick={() => {
                                                    copyToClipboard(
                                                        "Marianives Valsecchi"
                                                    );
                                                }}
                                            >
                                                Titolare: Marianives Valsecchi
                                            </span>
                                            <br />
                                            <span
                                                onClick={() => {
                                                    copyToClipboard(
                                                        "via Loverciano 15, 6874"
                                                    );
                                                }}
                                            >
                                                Indirizzo: via Loverciano 15,
                                                6874
                                            </span>
                                            <br />
                                            <span
                                                onClick={() => {
                                                    copyToClipboard(
                                                        "CH88 0900 0000 1520 5100 9"
                                                    );
                                                }}
                                            >
                                                Castel San Pietro IBAN: CH88
                                                0900 0000 1520 5100 9
                                            </span>
                                        </p>
                                    </>
                                )}
                                {paymentMethod === 1 && (
                                    <>
                                        <p style={{ margin: "0" }}>
                                            Cliccando sul testo puoi copiare
                                            l'informazioni sulla riga:
                                        </p>
                                        <p>
                                            <span
                                                onClick={() => {
                                                    copyToClipboard(
                                                        "079 546 92 70"
                                                    );
                                                }}
                                            >
                                                Numero di telefono (nonna Mary):
                                                079 546 92 70
                                            </span>
                                        </p>
                                    </>
                                )}
                                {!currentEvent.payed && (
                                    <>
                                        <WaringAlertBox>
                                            <span className="material-symbol fill">
                                                warning
                                            </span>
                                            <span>
                                                <b>Attenzione!</b>
                                                <br />
                                                Una volta effettuato il
                                                pagamento clicca sul bottone per
                                                registrare anche nell'app il
                                                pagamento!
                                            </span>
                                        </WaringAlertBox>
                                        <ButtonContainer>
                                            {trySaving && <Loader size={24} />}
                                            <FilledButton
                                                text="Salva"
                                                isDisabled={trySaving}
                                                onClick={handleOnPay}
                                            >
                                                <span className="material-symbol fill">
                                                    check
                                                </span>
                                            </FilledButton>
                                        </ButtonContainer>
                                    </>
                                )}
                            </ManageCard>
                        </ManageContent>
                    )}
                </ManageStateContainer>
            ) : (
                <EmptyStateContainer>
                    <h2>Seleziona prenotazione</h2>
                    <p>Seleziona una prenotazione per procedere.</p>
                    <NoEvent style={{ width: "100%", margin: "48px auto" }} />
                </EmptyStateContainer>
            )}
            <ModalDialogComponent
                ref={dialogRef}
                title="Elimina prenotazione"
                icon="delete"
                theme="var(--color-error)"
                cancelButton={{
                    text: "Annulla",
                    theme: "error",
                    onClick: () => dialogRef.current.close(),
                }}
                confirmButton={{
                    text: "Elimina",
                    theme: "error",
                    onClick: handleOnDelete,
                }}
            >
                <p className="body-medium">
                    Sei sicuro di voler eliminare la prenotazione?
                </p>
            </ModalDialogComponent>
            <FullScreenDialog ref={fullDialogRef} title="Checklist di pulizia">
                <p style={{ marginBottom: "24px" }}>
                    Per aiutarti a controllare la checklist, puoi mettere un
                    segno di spunta sui punti completati semplicemente cliccando
                    sulla voce.
                </p>
                <ChecklistTitle>
                    <span className="material-symbol fill">kitchen</span>
                    <h3>Cucina</h3>
                </ChecklistTitle>
                <ChecklistContent>
                    <CheckListItem
                        id="kitchen-1"
                        checkable={true}
                        label="Svuotare dalle proprie cose"
                    />
                    <CheckListItem
                        id="kitchen-2"
                        checkable={true}
                        label="Pulire il forno e le teglie"
                    />
                    <CheckListItem
                        id="kitchen-3"
                        checkable={true}
                        label="Svuotare, pulire e lasciare aperto il frigo (stessa cosa per il frigo di sotto)"
                    />
                    <CheckListItem
                        id="kitchen-4"
                        checkable={true}
                        label="Svuotare e lasciare aperta la lavastoviglie"
                    />
                    <CheckListItem
                        id="kitchen-5"
                        checkable={true}
                        label="Pulire il piano di lavoro e pensili Pulire il filtro della cappa"
                    />
                </ChecklistContent>
                <ChecklistTitle>
                    <span className="material-symbol fill">scene</span>
                    <h3>Salotto</h3>
                </ChecklistTitle>
                <ChecklistContent>
                    <CheckListItem
                        id="lounge-1"
                        checkable={true}
                        label="Pulire il divano e il mobile in pino, lasciare in ordine il piano del mobile"
                    />
                </ChecklistContent>
                <ChecklistTitle>
                    <span className="material-symbol fill">bed</span>
                    <h3>Camere</h3>
                </ChecklistTitle>
                <ChecklistContent>
                    <CheckListItem
                        id="bedroom-1"
                        checkable={true}
                        label="Pulire la camera"
                    />
                    <CheckListItem
                        id="bedroom-2"
                        checkable={true}
                        label="Svuotare dalle proprie cose"
                    />
                    <CheckListItem
                        id="bedroom-3"
                        checkable={true}
                        label="Sfoderare e riporre le coperte e i cuscini al loro posto"
                    />
                    <CheckListItem
                        id="bedroom-4"
                        checkable={true}
                        label="Far arieggiare i materiassi (mansarda)"
                    />
                </ChecklistContent>
                <ChecklistTitle>
                    <span className="material-symbol fill">shower</span>
                    <h3>Bagni</h3>
                </ChecklistTitle>
                <ChecklistContent>
                    <CheckListItem
                        id="bathroom-1"
                        checkable={true}
                        label="Pulire i bagni"
                    />
                    <CheckListItem
                        id="bathroom-2"
                        checkable={true}
                        label="Pulire la doccia"
                    />
                    <CheckListItem
                        id="bathroom-3"
                        checkable={true}
                        label="Svuotare dalle proprie cose"
                    />
                    <CheckListItem
                        id="bathroom-4"
                        checkable={true}
                        label="Chiudere il rubinetto principale dell'acqua e svuotare i vari rubinetti, gabinetti e docce"
                    />
                </ChecklistContent>
                <ChecklistTitle>
                    <span className="material-symbol fill">grass</span>
                    <h3>Esterno</h3>
                </ChecklistTitle>
                <ChecklistContent>
                    <CheckListItem
                        id="extern-1"
                        checkable={true}
                        label="Controlla di non aver lasciato niente in giro e che il gril sia pulito"
                    />
                </ChecklistContent>
            </FullScreenDialog>
        </Container>
    );
}

export default RecordViewPager;
